import React, { useRef, useState } from 'react';

import { AttachFile } from '@mui/icons-material';
import { Button, Chip, MenuItem, Paper, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';

import ChatterEditor from './ChatterEditor';
import { CHATTER_ATTACHMENT_ACCEPT, CHATTER_AUDIENCES, CHATTER_EDITOR_PLACEHOLDER } from './constants';
import { ChatterAttachment, ChatterDraft } from './types';
import { formatFileSize } from './utils';

interface Props {
  scopeLabel: string;
  onShare: (draft: ChatterDraft) => void;
}

export default function ChatterComposer({ scopeLabel, onShare }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [audience, setAudience] = useState(CHATTER_AUDIENCES[0]);
  const [text, setText] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  // Bumped after sharing to remount (clear) the uncontrolled editor.
  const [editorKey, setEditorKey] = useState(0);
  const [attachments, setAttachments] = useState<Array<ChatterAttachment>>([]);

  const canShare = text.length > 0 || attachments.length > 0;

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    setAttachments((prev) => [
      ...prev,
      ...files.map((file) => ({ id: `${file.name}-${file.size}-${Date.now()}`, name: file.name, size: file.size })),
    ]);
    event.target.value = '';
  };

  const resetDraft = () => {
    setText('');
    setEditorKey((prev) => prev + 1);
    setAttachments([]);
    setIsExpanded(false);
  };

  const handleShare = () => {
    onShare({ text, audience, attachments });
    resetDraft();
  };

  return (
    <Paper variant="outlined">
      <Tabs value="post" sx={{ px: 1 }}>
        <Tab value="post" label="Post" />
      </Tabs>
      {!isExpanded ? (
        <Stack direction="row" spacing={1} p={2}>
          <TextField
            fullWidth
            size="small"
            placeholder={CHATTER_EDITOR_PLACEHOLDER}
            onClick={() => setIsExpanded(true)}
            onFocus={() => setIsExpanded(true)}
            slotProps={{
              htmlInput: { 'aria-label': CHATTER_EDITOR_PLACEHOLDER, style: { textAlign: 'center' } },
            }}
          />
          <Button variant="contained" color="primary" disabled>
            Share
          </Button>
        </Stack>
      ) : (
        <Stack spacing={2} p={2}>
          <ChatterEditor key={editorKey} placeholder={CHATTER_EDITOR_PLACEHOLDER} onChange={setText} autoFocus />

          {attachments.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {attachments.map((file) => (
                <Chip
                  key={file.id}
                  icon={<AttachFile />}
                  label={`${file.name} (${formatFileSize(file.size)})`}
                  onDelete={() => setAttachments((prev) => prev.filter((current) => current.id !== file.id))}
                />
              ))}
            </Stack>
          )}

          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2">To</Typography>
              <TextField
                select
                size="small"
                value={audience}
                onChange={(event) => setAudience(event.target.value)}
                slotProps={{ htmlInput: { 'aria-label': 'Post audience' } }}
              >
                {CHATTER_AUDIENCES.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <Button variant="text" startIcon={<AttachFile />} onClick={() => fileInputRef.current?.click()}>
                Attach files
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                accept={CHATTER_ATTACHMENT_ACCEPT}
                onChange={handleFilesSelected}
                aria-label="Upload files"
              />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="caption" color="text.secondary">
                This feed is only for this {scopeLabel}.
              </Typography>
              <Button variant="text" onClick={resetDraft}>
                Cancel
              </Button>
              <Button variant="contained" color="primary" disabled={!canShare} onClick={handleShare}>
                Share
              </Button>
            </Stack>
          </Stack>
        </Stack>
      )}
    </Paper>
  );
}
